import BlotFormatter, { Action as BlotFormatterAction, IframeVideoSpec, ImageSpec, ToolbarButton as BlotFormatterToolbarButton } from "@enzedonline/quill-blot-formatter2";
import "@enzedonline/quill-blot-formatter2/dist/css/quill-blot-formatter2.css";
import { Editor, EditorTextChangeEvent } from "primereact/editor";
import Quill from "quill";
import QuillImageFormat from "quill/formats/image";
import { sanitize as sanitizeUrl } from "quill/formats/link";
import Delta from "quill-delta";
import QuillTableBetter from "quill-table-better";
import "quill-table-better/dist/quill-table-better.css";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { checkUploadableFiles, IMediaAssetModel, MEDIA_TYPE, MediaAssetService } from "../../../../Services/MediaAssetService";
import { resolveInlineMediaImages, stripInlineMediaImageSources } from "../../../../Util/resolveInlineMediaImages";
import { sanitizeRichTextHtml } from "../../../../Util/sanitizeRichTextHtml";
import { resolveApiErrorMessage } from "../../../../Util/apiErrorUtil";
import "./WysiwygEditor.scss";

export interface WysiwygEditorProps {
	value: string | undefined;
	editMode?: boolean;
	onChange: (value: string) => void;
	/** Needed to upload inline images to the right article's bucket — the image toolbar button is disabled
	 *  (with a "save first" hint) until this is available, same constraint as the media attachments card. */
	knowledgeArticleUuid?: string;
	/** The TEXT section this editor instance belongs to — every inline image gets scoped to it via
	 *  MediaAsset.sectionUuid, same requirement as knowledgeArticleUuid above. */
	sectionUuid?: string;
	/** Composing a brand-new article: knowledgeArticleUuid/sectionUuid don't exist yet, so an inserted image
	 *  can't upload immediately. When set, insertion instead embeds the image as a local blob: URL tagged
	 *  with a generated id (`data-pending-upload-id`) and hands the File back here — the caller (
	 *  DraftSectionEditor/KnowledgeArticleEditor) stages it and uploads it once the article/section are
	 *  actually created, then rewrites the content to swap the blob: URL for the real one. Ignored once
	 *  knowledgeArticleUuid+sectionUuid are both set (the immediate-upload path always wins). */
	onLocalImageInsert?: (localId: string, file: File) => void;
	/** Fired after an immediate (already-saved article) inline image upload succeeds, so the caller reloads
	 *  the article's media — the uploaded PHOTO is the same MediaAsset the attachments card shows, and its
	 *  signed URL reaches this editor through `mediaAssets`. */
	onImageUploaded?: () => void;
	/** The article's media (signed URLs included). Inline images are stored as `<img data-asset-uuid>`
	 *  without a real URL; they are pointed at the URLs in this list whenever it, or the content, changes. */
	mediaAssets?: IMediaAssetModel[];
	/** Editable-area minimum height, e.g. "520px" — the area grows taller than this to fit its content
	 *  (it never scrolls internally). Defaults to the original 320px so every other consumer of this shared
	 *  component keeps its current starting size. */
	height?: string;
	/** Adds a native drag handle to the bottom-right corner so the user can make the editor as big as they
	 *  want — opt-in, off by default for the same reason as `height`. */
	resizableHeight?: boolean;
}

/** Registered once per page load:
 *  - Extends Quill's built-in image format so `data-asset-uuid` survives round-tripping through Quill's
 *    model into stored HTML (Quill strips unrecognized attributes by default) — resolveInlineMediaImages
 *    later needs it to find the image's current signed URL. `width` is set the same way, by
 *    whichever resize UI is active (previously quill-resize-module, now blotFormatter2 below — both apply
 *    it as a plain `width` attribute, so this format didn't need to change when the resize module did).
 *  - blotFormatter2 replaces quill-resize-module for image resize/reposition (see
 *    knowledge-base-media-storage-progress.md uncertainty 6 — quill-resize-module was a maintenance risk
 *    and the source of a prior bug where its document-wide keydown listener ate Enter keystrokes).
 *  - quill-table-better adds table support (insert/merge/resize cells) — registered as a module here and
 *    triggered from the toolbar's own "insert table" button (handleInsertTableClick) rather than its
 *    built-in `toolbarTable` auto-injection, which expects the array-form `toolbar` option this editor
 *    doesn't use (headerTemplate supplies a custom toolbar DOM instead). */
let quillModulesRegistered = false;
function ensureQuillModulesRegistered() {
	if (quillModulesRegistered) {
		return;
	}
	const BaseImageFormat = Quill.import("formats/image") as typeof QuillImageFormat;
	class AssetTaggedImageFormat extends BaseImageFormat {
		static ATTRIBUTE_FORMATS = ["width", "data-asset-uuid", "data-pending-upload-id"];

		// Quill's own Image.sanitize() only whitelists http/https/data and silently replaces anything else
		// with the dead "//:0" URL — but a locally-staged image (onLocalImageInsert, before the article/
		// section exists to upload to) is previewed via URL.createObjectURL(file), a blob: URL. `<a href>`
		// protocol parsing also mishandles some blob: URLs, so allow those prefixes explicitly.
		static sanitize(url: string) {
			if (typeof url !== "string" || !url) {
				return "//:0";
			}
			if (url.startsWith("blob:") || url.startsWith("data:") || /^https?:/i.test(url)) {
				return url;
			}
			return sanitizeUrl(url, ["http", "https", "data", "blob"]) ? url : "//:0";
		}

		static create(value: string) {
			const node = super.create(value) as HTMLElement;
			if (typeof value === "string") {
				node.setAttribute("src", AssetTaggedImageFormat.sanitize(value));
			}
			return node;
		}

		static formats(domNode: HTMLElement) {
			const formats: Record<string, string> = {};
			AssetTaggedImageFormat.ATTRIBUTE_FORMATS.forEach((name) => {
				if (domNode.hasAttribute(name)) {
					formats[name] = domNode.getAttribute(name) as string;
				}
			});
			return formats;
		}

		format(name: string, value: string) {
			if (AssetTaggedImageFormat.ATTRIBUTE_FORMATS.includes(name)) {
				if (value) {
					this.domNode.setAttribute(name, value);
				} else {
					this.domNode.removeAttribute(name);
				}
			} else {
				super.format(name, value);
			}
		}
	}
	// Must run before BlotFormatter.registerFormats — it registers alignment formats on top of whatever
	// "formats/image" already resolves to, so our custom blot needs to be in place first.
	Quill.register(AssetTaggedImageFormat, true);
	BlotFormatter.registerFormats(Quill);
	Quill.register("modules/blotFormatter2", BlotFormatter, true);
	Quill.register({ "modules/table-better": QuillTableBetter }, true);
	quillModulesRegistered = true;
}

ensureQuillModulesRegistered();

const MOVE_UP_ICON = '<svg viewbox="0 0 18 18"><path class="ql-stroke" fill="none" d="M4,10 L9,5 L14,10 M9,5 L9,14"></path></svg>';
const MOVE_DOWN_ICON = '<svg viewbox="0 0 18 18"><path class="ql-stroke" fill="none" d="M4,8 L9,13 L14,8 M9,13 L9,4"></path></svg>';
const DRAG_HANDLE_ICON = '<svg viewbox="0 0 18 18"><circle class="ql-fill" cx="6" cy="4" r="1.3"/><circle class="ql-fill" cx="12" cy="4" r="1.3"/>'
	+ '<circle class="ql-fill" cx="6" cy="9" r="1.3"/><circle class="ql-fill" cx="12" cy="9" r="1.3"/>'
	+ '<circle class="ql-fill" cx="6" cy="14" r="1.3"/><circle class="ql-fill" cx="12" cy="14" r="1.3"/></svg>';
const DRAG_THRESHOLD_PX = 6;
const DROP_INDICATOR_CLASS = "blot-formatter__drop-indicator";

const isImageMoveChrome = (target: EventTarget | null): boolean =>
	target instanceof HTMLElement
	&& Boolean(target.closest(".blot-formatter__resize-handle, .blot-formatter__toolbar"));

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Quill.find isn't typed for a plain DOM node
const findBlotFromElement = (quill: any, element: HTMLElement): any => {
	const formatter = quill.getModule?.("blotFormatter2");
	if (formatter?.Quill?.find) {
		return formatter.Quill.find(element);
	}
	return quill.constructor.find?.(element) ?? null;
};

/** Repositions the image's whole containing paragraph to a different point in the document — there's no
 *  native or library support for this (blotFormatter2 only resizes/aligns/links/captions), and native browser
 *  drag-and-drop within contenteditable is unreliable and fights blotFormatter2's own overlay for mouse events
 *  (confirmed live: a real drag gesture on a selected image just re-selected it). Dragging the photo itself,
 *  the overlay drag handle, and the up/down buttons all funnel into `moveBlockToIndex`, which operates on
 *  Delta ranges (quill.getContents/updateContents) rather than raw DOM/HTML, so every format on the moved
 *  paragraph — the image's own attributes, any surrounding text formatting — survives unchanged. Moving
 *  recreates blots, so the overlay (pointing at the now-detached old image node) is closed afterwards
 *  rather than trying to re-select it. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Quill/Parchment blot internals are untyped
const moveBlockToIndex = (quill: any, block: any, targetIndex: number) => {
	const sourceIndex = quill.getIndex(block);
	const sourceLength = block.length();
	if (targetIndex >= sourceIndex && targetIndex <= sourceIndex + sourceLength) {
		return;
	}
	const sourceDelta = quill.getContents(sourceIndex, sourceLength);
	quill.deleteText(sourceIndex, sourceLength, "user");
	const adjustedTarget = targetIndex > sourceIndex ? targetIndex - sourceLength : targetIndex;
	quill.updateContents(new Delta().retain(adjustedTarget).concat(sourceDelta), "user");
};

// Top-level children of quill.root are exactly Quill's own "blocks" (paragraphs, headings, ...) — the same
// unit `moveUp`/`moveDown` operate on — so the nearest one to the cursor is always a valid drop target.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Quill/Parchment blot internals are untyped
const findDropTarget = (quill: any, clientY: number, dragBlock: any) => {
	const children = Array.from(quill.root.children as HTMLCollection).filter(
		(el) => el !== dragBlock?.domNode,
	) as HTMLElement[];
	for (const child of children) {
		const rect = child.getBoundingClientRect();
		if (clientY < rect.top + rect.height / 2) {
			return { position: "before" as const, element: child, rect };
		}
	}
	const last = children[children.length - 1];
	return last ? { position: "after" as const, element: last, rect: last.getBoundingClientRect() } : null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Quill/Parchment blot internals are untyped
const startBlockDrag = (quill: any, block: any, startEvent: MouseEvent, onMoved: () => void) => {
	if (!block || startEvent.button !== 0) {
		return;
	}
	const startX = startEvent.clientX;
	const startY = startEvent.clientY;
	let dragging = false;
	let dropIndicator: HTMLElement | null = null;
	const editorEl = quill.container?.closest?.(".wysiwyg-editor") as HTMLElement | null;

	const cleanup = () => {
		document.removeEventListener("mousemove", onMove);
		document.removeEventListener("mouseup", onUp);
		dropIndicator?.remove();
		dropIndicator = null;
		editorEl?.classList.remove("wysiwyg-editor--dragging");
	};

	const onMove = (event: MouseEvent) => {
		const distance = Math.hypot(event.clientX - startX, event.clientY - startY);
		if (!dragging) {
			if (distance < DRAG_THRESHOLD_PX) {
				return;
			}
			dragging = true;
			event.preventDefault();
			dropIndicator = document.createElement("div");
			dropIndicator.className = DROP_INDICATOR_CLASS;
			quill.container.appendChild(dropIndicator);
			editorEl?.classList.add("wysiwyg-editor--dragging");
		}
		const target = findDropTarget(quill, event.clientY, block);
		if (!target || !dropIndicator) {
			return;
		}
		const containerRect = quill.container.getBoundingClientRect();
		const y = (target.position === "before" ? target.rect.top : target.rect.bottom) - containerRect.top;
		dropIndicator.style.top = `${y}px`;
	};

	const onUp = (event: MouseEvent) => {
		const didDrag = dragging;
		cleanup();
		if (!didDrag) {
			return;
		}
		const target = findDropTarget(quill, event.clientY, block);
		if (!target) {
			return;
		}
		const targetBlot = findBlotFromElement(quill, target.element);
		if (!targetBlot) {
			return;
		}
		const targetIndex = target.position === "before"
			? quill.getIndex(targetBlot)
			: quill.getIndex(targetBlot) + targetBlot.length();
		moveBlockToIndex(quill, block, targetIndex);
		onMoved();
	};

	document.addEventListener("mousemove", onMove);
	document.addEventListener("mouseup", onUp);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Quill instance from getQuill()/getModule is untyped
const bindImageBlockDrag = (quill: any): (() => void) => {
	const onNativeDragStart = (event: DragEvent) => {
		if (event.target instanceof HTMLImageElement) {
			event.preventDefault();
		}
	};
	const onMouseDown = (event: MouseEvent) => {
		if (event.button !== 0 || isImageMoveChrome(event.target)) {
			return;
		}
		const target = event.target;
		if (!(target instanceof HTMLElement)) {
			return;
		}
		const img = target.closest("img");
		if (!img || !quill.root.contains(img)) {
			return;
		}
		const blot = findBlotFromElement(quill, img);
		if (!blot) {
			return;
		}
		const block = quill.getLine(quill.getIndex(blot))[0];
		startBlockDrag(quill, block, event, () => quill.getModule("blotFormatter2")?.hide());
	};
	quill.root.addEventListener("dragstart", onNativeDragStart);
	quill.root.addEventListener("mousedown", onMouseDown);
	return () => {
		quill.root.removeEventListener("dragstart", onNativeDragStart);
		quill.root.removeEventListener("mousedown", onMouseDown);
	};
};

class MoveAction extends BlotFormatterAction {
	private handle: HTMLElement | null = null;

	onCreate = () => {
		this.toolbarButtons = [
			new BlotFormatterToolbarButton("moveUp", this.moveUp, this.formatter.options.toolbar),
			new BlotFormatterToolbarButton("moveDown", this.moveDown, this.formatter.options.toolbar),
		];
		this.handle = document.createElement("span");
		this.handle.innerHTML = DRAG_HANDLE_ICON;
		this.handle.className = this.formatter.options.toolbar.buttonClassName;
		this.handle.style.cursor = "grab";
		const dragTooltip = this.formatter.options.toolbar.tooltips?.dragHandle;
		if (dragTooltip) {
			this.handle.title = dragTooltip;
		}
		this.handle.addEventListener("mousedown", this.onHandleMouseDown);
		// this.formatter.toolbar already exists (built once in BlotFormatter's own constructor) by the time any
		// action's onCreate runs; Toolbar.create() (which runs right after this) appends its own buttons via
		// .append(), additively, so appending here first just makes the handle the toolbar's first icon.
		this.formatter.toolbar.element.appendChild(this.handle);
		// Overlay sits on top of the selected image; without this, dragging the photo hits blotFormatter2's
		// overlay (which starts resize) instead of moving the block. Capture so we beat ResizeAction.
		this.formatter.overlay.addEventListener("mousedown", this.onOverlayMouseDown, true);
	};

	onDestroy = () => {
		this.handle?.removeEventListener("mousedown", this.onHandleMouseDown);
		this.handle?.remove();
		this.handle = null;
		this.formatter.overlay.removeEventListener("mousedown", this.onOverlayMouseDown, true);
	};

	moveUp = () => {
		const quill = this.formatter.quill;
		const block = this.getBlock();
		const prev = block?.prev;
		if (!block || !prev) {
			return;
		}
		moveBlockToIndex(quill, block, quill.getIndex(prev));
		this.formatter.hide();
	};

	moveDown = () => {
		const quill = this.formatter.quill;
		const block = this.getBlock();
		const next = block?.next;
		if (!block || !next) {
			return;
		}
		moveBlockToIndex(quill, block, quill.getIndex(next) + next.length());
		this.formatter.hide();
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Quill/Parchment blot internals are untyped
	private getBlock = (): any => {
		const quill = this.formatter.quill;
		const targetBlot = this.formatter.currentSpec?.getTargetBlot();
		return targetBlot ? quill.getLine(quill.getIndex(targetBlot))[0] : null;
	};

	private onHandleMouseDown = (event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
		const block = this.getBlock();
		startBlockDrag(this.formatter.quill, block, event, () => this.formatter.hide());
	};

	private onOverlayMouseDown = (event: MouseEvent) => {
		if (event.button !== 0 || isImageMoveChrome(event.target)) {
			return;
		}
		event.stopPropagation();
		const block = this.getBlock();
		startBlockDrag(this.formatter.quill, block, event, () => this.formatter.hide());
	};
}

class MoveableImageSpec extends ImageSpec {
	constructor(formatter: BlotFormatter) {
		super(formatter);
		// ImageSpec assigns `getActions` as its own arrow-function instance field (not a prototype method), so
		// `super.getActions()` isn't reachable from here — capture the field ImageSpec's own constructor just
		// set on `this` and wrap it, instead.
		const baseGetActions = this.getActions;
		this.getActions = () => [...baseGetActions(), new MoveAction(this.formatter)];
	}
}

const NO_MEDIA_ASSETS: IMediaAssetModel[] = [];

const WysiwygEditor = ({
	value,
	editMode = false,
	onChange,
	knowledgeArticleUuid,
	sectionUuid,
	onLocalImageInsert,
	onImageUploaded,
	mediaAssets = NO_MEDIA_ASSETS,
	height = "320px",
	resizableHeight = false,
}: WysiwygEditorProps) => {
	const { t } = useTranslation();
	const editorRef = useRef<Editor>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	const previewRef = useRef<HTMLDivElement>(null);
	const mediaAssetService = useMemo(() => new MediaAssetService(), []);
	const mediaAssetsRef = useRef(mediaAssets);
	mediaAssetsRef.current = mediaAssets;
	// Only the read-only view renders `value` as HTML; while editing, Quill owns the content.
	const sanitizedValue = useMemo(() => (editMode ? "" : sanitizeRichTextHtml(value)), [editMode, value]);

	useEffect(() => {
		ensureQuillModulesRegistered();
	}, []);

	useEffect(() => {
		if (!editMode) {
			return undefined;
		}
		let unbind: (() => void) | undefined;
		let cancelled = false;
		const attach = (): boolean => {
			const quill = editorRef.current?.getQuill();
			if (!quill || cancelled || unbind) {
				return Boolean(unbind);
			}
			unbind = bindImageBlockDrag(quill);
			resolveInlineMediaImages(quill.root, mediaAssetsRef.current);
			return true;
		};
		if (attach()) {
			return () => {
				cancelled = true;
				unbind?.();
			};
		}
		let attempts = 0;
		const timer = window.setInterval(() => {
			attempts += 1;
			if (attach() || attempts > 40) {
				window.clearInterval(timer);
			}
		}, 50);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
			unbind?.();
		};
	}, [editMode]);

	const toNbsp = (text: string | undefined) => {
		return text?.replace(/ {2,}/g, (match) => '&nbsp;'.repeat(match.length)) || '';
	};

	const fromNbsp = (text: string | undefined) => {
		return text?.replace(/(&nbsp;)+/g, (match) => ' '.repeat(match.length / 6)) || '';
	};

	/** PrimeReact's Editor is controlled: whenever its `value` prop changes while Quill itself doesn't have
	 *  DOM focus, it calls `quill.setContents(quill.clipboard.convert({html: value}))` — a full reparse that
	 *  tears down and rebuilds every node in the document. table-better's cell menus and blotFormatter2's
	 *  resize handles both live in an overlay outside `.ql-editor`, so committing a table cell's background
	 *  color or dragging an image resize handle takes DOM focus away from Quill; the resulting text-change
	 *  still flows up through onChange/value here, and without this guard that would echo straight back into
	 *  the reparse above mid-interaction, desyncing table-better's internal table/cell bookkeeping (table
	 *  vanishes) or replacing the image node blotFormatter2 is actively dragging (resize appears to do
	 *  nothing). Only forward `value` into the Editor when it changed for a reason other than our own last
	 *  emitted change, so a module-driven edit is left alone once Quill already has it. */
	const lastEmittedValueRef = useRef<string | undefined>(value);
	const [editorValue, setEditorValue] = useState<string | undefined>(() => toNbsp(value));

	useEffect(() => {
		if (value !== lastEmittedValueRef.current) {
			lastEmittedValueRef.current = value;
			setEditorValue(toNbsp(value));
		}
	}, [value]);

	// Point the inline images at their current signed URLs: after the content was (re)parsed, and when the
	// media list was reloaded (an upload, or URLs that were about to expire).
	useEffect(() => {
		resolveInlineMediaImages(editMode ? editorRef.current?.getQuill()?.root : previewRef.current, mediaAssets);
	}, [editMode, editorValue, sanitizedValue, mediaAssets]);

	/** Clicks on the min-height chrome around Quill (padding of .p-editor-content / .ql-container, not the
	 *  first text line) still need to put the caret in the document. A click that already landed in
	 *  `.ql-editor` or on toolbar / overlay chrome is left alone so native caret placement and image
	 *  drag keep working. */
	const handleEditorSurfaceMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
		if (!editMode) {
			return;
		}
		const target = event.target;
		if (!(target instanceof HTMLElement)) {
			return;
		}
		if (target.closest(".ql-editor, .ql-toolbar, .p-editor-toolbar, .blot-formatter__overlay, .blot-formatter__toolbar, button, input, select, textarea, a")) {
			return;
		}
		const quill = editorRef.current?.getQuill();
		if (!quill) {
			return;
		}
		event.preventDefault();
		quill.focus();
		if (!quill.getSelection()) {
			quill.setSelection(Math.max(0, quill.getLength() - 1), 0, "silent");
		}
	};

	const handleTextChange = (e: EditorTextChangeEvent) => {
		// Signed URLs stay out of what gets saved. Quill also reports the `src` swap above as a change; with
		// the URLs stripped that leaves the content exactly as it was, and there is nothing to pass on.
		const nextValue = fromNbsp(stripInlineMediaImageSources(e.htmlValue ?? ""));
		if (nextValue === lastEmittedValueRef.current) {
			return;
		}
		lastEmittedValueRef.current = nextValue;
		onChange(nextValue);
	}

	const handleInsertTableClick = () => {
		const quill = editorRef.current?.getQuill();
		const tableModule = quill?.getModule("table-better");
		if (!tableModule) {
			return;
		}
		tableModule.insertTable(3, 3);
	};

	const canUploadImmediately = Boolean(knowledgeArticleUuid && sectionUuid);
	const canInsertImage = canUploadImmediately || Boolean(onLocalImageInsert);

	const handleInsertImageClick = () => {
		if (!canInsertImage) {
			toast.error(t("knowledgeBase.form.mediaSaveFirst") as string);
			return;
		}
		fileInputRef.current?.click();
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Quill instance type from getQuill() is untyped
	const insertImageAtSelection = (quill: any, src: string, extraFormats: Record<string, string>) => {
		const range = quill.getSelection(true) ?? { index: quill.getLength(), length: 0 };
		quill.insertEmbed(range.index, "image", src, "user");
		const leaf = quill.getLeaf(range.index)?.[0];
		const imageNode = leaf?.domNode;
		if (imageNode instanceof HTMLImageElement) {
			// Default Image.sanitize rewrites blob: to "//:0" when our custom blot isn't the one that
			// created the node (PrimeReact constructs Quill before this module's register can stick on
			// that instance). Force the real preview URL onto the DOM.
			imageNode.setAttribute("src", src);
			Object.entries(extraFormats).forEach(([name, value]) => imageNode.setAttribute(name, value));
			if (!imageNode.getAttribute("width")) {
				imageNode.setAttribute("width", "50%");
			}
		}
		quill.formatText(range.index, 1, { width: "50%", ...extraFormats }, "user");
		quill.setSelection(range.index + 1, 0, "user");
	};

	/** The upload succeeded: the local preview becomes a real inline image. Its `src` stays the local blob
	 *  until the reloaded media list brings the signed URL; what is saved never carries either of them. */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Quill instance isn't typed for this API
	const upgradePendingImage = (quill: any, localId: string, assetUuid: string) => {
		const imageNode = quill.root.querySelector(`img[data-pending-upload-id="${localId}"]`);
		if (!(imageNode instanceof HTMLImageElement)) {
			return;
		}
		imageNode.setAttribute("data-asset-uuid", assetUuid);
		imageNode.removeAttribute("data-pending-upload-id");
		quill.update("user");
	};

	/** The upload failed: take the local preview out again, or the text keeps an image that exists nowhere. */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Quill instance isn't typed for this API
	const removePendingImage = (quill: any, localId: string) => {
		const imageNode = quill.root.querySelector(`img[data-pending-upload-id="${localId}"]`);
		const blot = imageNode instanceof HTMLElement ? findBlotFromElement(quill, imageNode) : null;
		if (blot) {
			quill.deleteText(quill.getIndex(blot), 1, "user");
		}
	};

	const handleImageFileSelected = async (file: File | null) => {
		if (!file) {
			return;
		}
		const { violations } = await checkUploadableFiles([file]);
		if (violations.length > 0) {
			toast.error(t(violations[0]) as string);
			return;
		}
		const quill = editorRef.current?.getQuill();
		if (!quill) {
			return;
		}
		const localId = crypto.randomUUID();
		const blobUrl = URL.createObjectURL(file);
		insertImageAtSelection(quill, blobUrl, { "data-pending-upload-id": localId });
		if (canUploadImmediately && knowledgeArticleUuid && sectionUuid) {
			try {
				const uploadResponse = await mediaAssetService.uploadMediaAsset(knowledgeArticleUuid, MEDIA_TYPE.PHOTO, file, sectionUuid);
				upgradePendingImage(quill, localId, uploadResponse.data.data);
				onImageUploaded?.();
			} catch (error) {
				removePendingImage(quill, localId);
				toast.error(resolveApiErrorMessage(error, "knowledgeBase.form.mediaUploadError", t));
			}
			return;
		}
		if (onLocalImageInsert) {
			onLocalImageInsert(localId, file);
		}
	};

	return (<>
		<div className="h-full no-drag" onMouseDown={handleEditorSurfaceMouseDown}>
			{!editMode && value && (
				<div ref={previewRef} className="textField quill-content"
					dangerouslySetInnerHTML={{ __html: sanitizedValue }} />
			)}
			{!editMode && !value && (<>
				<div className="flex h-full align-items-center justify-content-center">
					<div className="flex flex-column align-items-center">
						<i className="pi pi-info-circle text-4xl mb-4" />
						<span className="text-xl">{t('common.empty')}</span>
					</div>
				</div>
			</>)}
			{editMode && (
				<>
					<Editor
						ref={editorRef}
						value={editorValue}
						disabled={!editMode}
						onTextChange={handleTextChange}
						className={`wysiwyg-editor${resizableHeight ? " wysiwyg-editor--resizable" : ""}`}
						modules={{
							blotFormatter2: {
								specs: [MoveableImageSpec, IframeVideoSpec],
								toolbar: {
									icons: { moveUp: MOVE_UP_ICON, moveDown: MOVE_DOWN_ICON },
									tooltips: {
										moveUp: t("knowledgeBase.form.moveImageUp") as string,
										moveDown: t("knowledgeBase.form.moveImageDown") as string,
										dragHandle: t("knowledgeBase.form.dragImage") as string,
									},
								},
							},
							// Quill's own rudimentary `table` module is unrelated to table-better and would
							// otherwise still register — disabled so table-better is the only table handling.
							table: false,
							"table-better": {
								language: "en_US",
								menus: ["column", "row", "merge", "table", "cell", "wrap", "copy", "delete"],
							},
							keyboard: {
								bindings: QuillTableBetter.keyboardBindings,
							},
						}}
						headerTemplate={
							<>
								<span className="ql-formats">
									<select className="ql-header">
										<option value="1" />
										<option value="2" />
										<option value="3" />
										<option selected />
									</select>
								</span>
								<span className="ql-formats">
									<button className="ql-bold" />
									<button className="ql-italic" />
									<button className="ql-underline" />
									<button className="ql-strike" />
								</span>
								<span className="ql-formats">
									<select className="ql-color" />
									<select className="ql-background" />
								</span>
								<span className="ql-formats">
									<button className="ql-list" value="ordered" />
									<button className="ql-list" value="bullet" />
								</span>
								<span className="ql-formats">
									<select className="ql-align" />
								</span>
								<span className="ql-formats">
									<button type="button" onClick={handleInsertImageClick} title={t("knowledgeBase.form.insertImage") as string}>
										<i className="pi pi-image" />
									</button>
									<button type="button" onClick={handleInsertTableClick} title={t("knowledgeBase.form.insertTable") as string}>
										<i className="pi pi-table" />
									</button>
								</span>
								<span className="ql-formats">
									<button className="ql-clean"></button>
								</span>
							</>
						}
						style={{ minHeight: height }}
					/>
					<input
						ref={fileInputRef}
						type="file"
						hidden
						accept="image/*"
						onChange={(e) => {
							handleImageFileSelected(e.target.files?.[0] ?? null);
							e.target.value = "";
						}}
					/>
				</>
			)}
		</div>
	</>);
};

export default WysiwygEditor;
